import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Refresca una query cuando la ventana recupera el foco.
 *
 * Criterio de calibración del polling (2026-08-18): un dato que cambia cuando
 * el usuario hace algo —o cuando vuelve de autorizar algo afuera— no se
 * descubre con un intervalo corto, se descubre cuando el usuario VUELVE. El
 * intervalo automático queda como red por si el cambio llega de un tercero,
 * no como mecanismo principal.
 *
 * Se escucha `visibilitychange` además de `focus` porque volver de una pestaña
 * de OAuth en el mismo navegador dispara el primero y no siempre el segundo.
 */
export function useRefetchOnFocus(queryKey: readonly unknown[], enabled = true): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const refetch = () => { void qc.invalidateQueries({ queryKey }); };
    const onVisible = () => { if (document.visibilityState === 'visible') refetch(); };
    window.addEventListener('focus', refetch);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refetch);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, enabled, JSON.stringify(queryKey)]);
}
